import { z } from 'zod';

import type { ClaimStatus, WarrantyReason } from '@/features/requests';

/**
 * The two wire enums. **Do not "fix" these into value imports from `@/features/requests`**, which
 * exports identical schemas: that barrel re-exports `WarrantyFilterDrawer`, which imports the
 * `@/features/orders` barrel — an orders↔requests cycle. Vite resolves every import in a module
 * before defining any export binding, so this file re-enters the requests barrel mid-evaluation, the
 * schema bindings are still `undefined`, and `z.enum(undefined)` throws at import time. Export order
 * does not fix it (tried). `replacements/schemas.ts` survives its value import only because nothing
 * evaluates it inside the cycle.
 *
 * The **type-only** import is erased, so `satisfies` pins both lists to the queue's canonical unions
 * for free: a renamed or invented token fails typecheck, and a token the backend adds later fails
 * the parse loudly as `contract_drift`.
 */
const claimStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
] as const satisfies readonly ClaimStatus[]);

export const warrantyReasonSchema = z.enum([
  'mechanical_or_electrical_failure',
  'manufacturing_defects',
  'performance_failure',
  'structural_flaws',
  'others',
] as const satisfies readonly WarrantyReason[]);

/** One row of the per-order Warranty Claims listing (`GET /orders/admin/:orderId/warranty-claims`).
 *
 *  `category` stays nullable in the type because BE types it `string | null`, but it is now
 *  *populated* for bundle components too (BE on 112445, 2026-08-04: "no fallback to bundle name is
 *  needed"), so the subtitle renders `category ?? '—'` and no longer substitutes `bundleName`.
 *
 *  `quantity` is **required** — BE sends it non-null on both the list and the detail (FLAG-5, added
 *  2026-07-29 and confirmed 2026-08-04). It drives the Unit suffix, so a silent absence would mean
 *  two claims against different units of one line item rendering identically; a loud parse failure is
 *  the better trade.
 *
 *  `bundleDeviceId` is parsed and unrendered — it is what makes a bundle component individually
 *  addressable, and ticket 02's three-part unit key needs it. */
export const orderWarrantyClaimRowSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  bundleName: z.string().nullable(),
  category: z.string().nullable(),
  unitIndex: z.number(),
  quantity: z.number(),
  bundleDeviceId: z.string().nullable(),
  status: claimStatusSchema,
  requestedAt: z.string(),
});

/** The whole listing response. `hasEligibleDevices` is the backend's eligibility verdict — the
 *  warranty analogue of the replacement listing's `isWithinWindow`. It drives ticket 02's routing and
 *  CTA; this ticket parses it and renders nothing from it. */
export const orderWarrantyClaimListSchema = z.object({
  hasEligibleDevices: z.boolean(),
  claims: z.array(orderWarrantyClaimRowSchema),
});

/** One evidence image uploaded with the claim. `sortOrder` is parsed but unused — the array already
 *  arrives ordered; re-sorting client-side would be a second source of truth. Module-local: only the
 *  detail schema below composes it, and `OrderWarrantyClaimDetail` carries the images inline. */
const orderWarrantyClaimImageSchema = z.object({
  id: z.string(),
  /** Nullable because the BE presigns at read time and answers `null` when that throws
   *  (`warranty` types: `url: string | null`). Same defect class as ADO 119316 on the replacement
   *  detail: a required string let one unshowable evidence photo reject the whole 200 and render
   *  the drawer's load-error copy. The drawer drops a url-less image instead. */
  url: z.string().nullable(),
  originalFileName: z.string().nullable(),
  sortOrder: z.number(),
});

/** One timeline event as the **wire** names it: `timeline: [{ status, timestamp }]`, confirmed by BE
 *  on 112445 (2026-08-04) when they added the field. `warrantyTimelineEvents` maps it onto the
 *  drawer's internal `{ status, at }`, so this is the only place the wire vocabulary appears.
 *
 *  Still tolerant-optional: a deployment that predates the field derives the timeline instead of
 *  failing the parse (ADR-0023, FLAG-4). `status` stays an un-narrowed string — BE types it
 *  `status: string`, not our four-value enum, so an added token reaches the drawer (which degrades it
 *  to a neutral chip) rather than breaking the parse. */
const orderWarrantyTimelineEntrySchema = z.object({
  status: z.string(),
  timestamp: z.string(),
});

/** Read-only detail behind the Ticket Preview drawer
 *  (`GET /orders/admin/:orderId/warranty-claims/:claimId`), consumed by tickets 04/05.
 *
 *  Not parsed, deliberately: `reasonLabel` (we render `WARRANTY_REASONS[reason]`, so one casing stays
 *  ours — ADR-0023, inherited from 0022) and `requestedBy` / `resolvedBy` (no actor in any AC or
 *  frame). zod strips unknown keys, so they cost nothing.
 *
 *  `imageUrl` — the Device's catalogue photograph — **is** parsed and rendered nowhere, so the wire
 *  shape stays documented; the thumbnails come from `images[]`, the uploaded evidence. */
export const orderWarrantyClaimDetailSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  bundleName: z.string().nullable(),
  category: z.string().nullable(),
  imageUrl: z.string().nullable(),
  unitIndex: z.number(),
  quantity: z.number(),
  bundleDeviceId: z.string().nullable(),
  reason: warrantyReasonSchema,
  description: z.string(),
  status: claimStatusSchema,
  requestedAt: z.string(),
  resolvedAt: z.string().nullable(),
  images: z.array(orderWarrantyClaimImageSchema),
  rejectionReason: z.string().nullish(),
  timeline: z.array(orderWarrantyTimelineEntrySchema).nullish(),
});

/** One eligible line item (`GET …/warranty-eligible-items`), consumed by ticket 02. `eligibleUnits` is
 *  gap-preserving — units with a Pending or Approved claim/replacement are simply absent and the
 *  survivors keep their original numbers. A bundle arrives as one row **per component device**, all
 *  sharing one `orderItemId` and distinguished by `bundleDeviceId`; the bundle itself is never
 *  returned, so "the bundle is NOT selectable" needs no frontend filtering (ADR-0023). */
export const warrantyEligibleItemSchema = z.object({
  orderItemId: z.string(),
  kind: z.string(),
  productName: z.string(),
  imageUrl: z.string().nullable(),
  quantity: z.number(),
  // null = no recorded warranty (legacy line / bundle member) — still claimable.
  warrantyMonths: z.number().nullable(),
  warrantyExpiresAt: z.string().nullable(),
  eligibleUnits: z.array(z.number()),
  bundleDeviceId: z.string().nullable(),
  bundleName: z.string().nullable(),
});

export const warrantyEligibleItemsSchema = z.array(warrantyEligibleItemSchema);

/** A file that finished uploading (the `<UploadField>` committed value shape). Declared locally, as
 *  `features/{orders,devices,bundles,components}/schemas.ts` and `orders/replacements/schemas.ts`
 *  each do. */
const uploadedFileSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string(),
});

/**
 * Intake form values. `device` is the composite `${orderItemId}#${unitIndex}#${bundleDeviceId ?? ''}`
 * option value produced by `format.ts`, split apart at submit (ADR-0023). Unlike
 * `requestReplacementSchema`'s `superRefine` on `reason === 'others'`, `description` is
 * unconditionally required (AC 112444). `.trim()` runs before `.min(1)`, so a whitespace-only
 * description is rejected and the resolver hands `onSubmit` an already-trimmed value. `images` is
 * one to five — the lower bound has no `UploadPreset` equivalent, so it is enforced here alone.
 */
export const claimWarrantySchema = z.object({
  device: z.string().min(1, 'Select a device'),
  reason: warrantyReasonSchema,
  description: z.string().trim().min(1, 'Description is required').max(2000),
  images: z
    .array(uploadedFileSchema)
    .min(1, 'Add at least one image')
    .max(5, 'You can upload a maximum of 5 images'),
});
